import type { SpringConfig } from '@react-spring/web';
import { animated, useTransition } from '@react-spring/web';
import { useRef } from 'react';
import { Outlet, matchPath, useLocation, useOutlet } from 'react-router';
import styled from 'styled-components';

import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { useIsMobileApp } from '#src/hooks/useIsMobileApp.ts';
import { usePrevious } from '#src/hooks/usePrevious.ts';

type OutletTransitionDirection = 'leftToRight' | 'rightToLeft';

export interface OutletTransition {
  from: string;
  to: string;
  direction: OutletTransitionDirection;
  reversable: boolean;
}

const reverse = (direction: OutletTransitionDirection | undefined) => {
  if (!direction) return undefined;
  return direction === 'leftToRight' ? 'rightToLeft' : 'leftToRight';
};

const CONFIG: SpringConfig = {
  mass: 1, // Lower mass makes the animation feel lighter
  tension: 170, // Reduced tension for a gentler acceleration
  friction: 26, // Increased friction for a smoother stop
  clamp: false, // Allowing the animation to overshoot slightly and then settle
};

const Wrapper = styled(Horizontal)`
  position: relative;
  & > * {
    width: 100%;
  }
  &[data-is-animating='true'] {
    &.rightToLeft {
      & > *:nth-child(2) {
        background: var(--c-black);
        position: absolute;
        inset: 0;
      }
    }

    &.leftToRight {
      & > *:nth-child(1) {
        background: var(--c-black);
        position: absolute;
        z-index: 1;
        inset: 0;
      }
    }
  }
`;

const AnimatedOutletComponent = ({
  direction,
}: {
  direction: OutletTransitionDirection | undefined;
}) => {
  const location = useLocation();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const rightToLeft = useTransition(location.pathname, {
    initial: null,
    from: { transform: 'translate3d(100%,0,0)' },
    enter: { transform: 'translate3d(0%,0,0)' },
    leave: { transform: 'translate3d(-50%,0,0)' },
    config: CONFIG,
    onStart: () => {
      if (wrapperRef.current) {
        wrapperRef.current.dataset.isAnimating = 'true';
      }
    },
    onDestroyed: () => {
      if (wrapperRef.current) {
        delete wrapperRef.current.dataset.isAnimating;
      }
    },
  });

  const leftToRight = useTransition(location.pathname, {
    initial: null,
    from: { transform: 'translate3d(-50%,0,0)' },
    enter: { transform: 'translate3d(0%,0,0)' },
    leave: { transform: 'translate3d(100%,0,0)' },
    config: CONFIG,
    onStart: () => {
      if (wrapperRef.current) {
        wrapperRef.current.dataset.isAnimating = 'true';
      }
    },
    onDestroyed: () => {
      if (wrapperRef.current) {
        delete wrapperRef.current.dataset.isAnimating;
      }
    },
  });

  const outlet = useOutlet();
  const previousOutlet = usePrevious(outlet);

  if (!direction) return outlet;

  const animation = direction === 'leftToRight' ? leftToRight : rightToLeft;
  return (
    <Wrapper className={direction} ref={wrapperRef}>
      {animation((styles, pathname) => (
        <animated.div style={styles}>
          {pathname === location.pathname ? outlet : previousOutlet}
        </animated.div>
      ))}
    </Wrapper>
  );
};

export const AnimatedOutlet = ({
  transitions,
}: {
  transitions: OutletTransition[];
}) => {
  const location = useLocation();
  const previousPathname = usePrevious(location.pathname);
  const { isMobileApp, slowDevice } = useIsMobileApp();

  if (!isMobileApp || slowDevice) {
    return <Outlet />;
  }

  const direction =
    !previousPathname || previousPathname === location.pathname
      ? undefined
      : transitions.find(
          t =>
            matchPath(t.from, previousPathname) &&
            matchPath(t.to, location.pathname)
        )?.direction ||
        reverse(
          transitions.find(
            t =>
              t.reversable &&
              matchPath(t.to, previousPathname) &&
              matchPath(t.from, location.pathname)
          )?.direction
        );

  return <AnimatedOutletComponent direction={direction} />;
};
